import React, { useState, useEffect } from "react";
import { Microscope, Wand2, Loader2 } from "lucide-react";
import DashboardModal from "@/components/ui/DashboardModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ResearchGenerationModal from "@/components/research-generator/ResearchGenerationModal";
import { usePrivateResearchGeneration } from "@/hooks/private-research-generator/usePrivateResearchGeneration";
import { useUser } from "@/hooks/auth/use-user";
import { supabase } from "@/integrations/supabase/client";

interface CreateResearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
  goalTitle: string;
  goalDescription?: string;
  onResearchCreated?: (researchId: string) => void;
  initialData?: {
    title?: string;
    description?: string;
  };
}

interface ResearchFormState {
  researchPrompt: string;
  description: string;
}

const CreateResearchModal: React.FC<CreateResearchModalProps> = ({
  isOpen,
  onClose,
  goalId,
  goalTitle,
  goalDescription,
  onResearchCreated,
  initialData
}) => {
  const { user } = useUser();
  
  // Use the private research generation hook
  const generateResearchMutation = usePrivateResearchGeneration();
  
  // Form state management
  const [formState, setFormState] = useState<ResearchFormState>({
    researchPrompt: initialData?.title || '',
    description: initialData?.description || ''
  });

  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [researchParams, setResearchParams] = useState<any>(null);

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      setFormState({
        researchPrompt: initialData?.title || '',
        description: initialData?.description || ''
  });
    }
  }, [isOpen, initialData?.title, initialData?.description]);

  // Form handlers
  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, researchPrompt: e.target.value }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormState(prev => ({ ...prev, description: e.target.value }));
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormState({
      researchPrompt: '',
      description: ''
    });
  };

  // Check if form is ready for submission
  const isReady = formState.researchPrompt.trim().length > 0;
  const isLoading = generateResearchMutation.isPending;

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // Handle research generation
  const handleGenerateResearch = async () => {
    if (!isReady || isLoading || !user) return;

    console.log('[CreateResearchModal] Starting research generation for project goal:', goalId);
    
    try {
      // Get the workbook_id for this goal
      const { data: goalData, error: goalError } = await supabase
        .from('planner_goals_workbooks')
        .select('workbook_id')
        .eq('goal_id', goalId)
        .eq('user_id', user.id)
        .single();
        
      if (goalError || !goalData?.workbook_id) {
        throw new Error('Could not find project for this goal');
      }

      // Prepare research parameters
      const params = {
        researchType: 'General Research',
        researchPrompt: formState.researchPrompt.trim(),
        timeframe: 'recent',
        workbook_id: goalData.workbook_id, // Direct project assignment via workbook_id
        selectedFilters: {
          additional_context: formState.description.trim() || undefined
        }
      };
      
      // Show progress modal
      setResearchParams(params);
      setShowProgressModal(true);
    } catch (error) {
      console.error('[CreateResearchModal] Error preparing research generation:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start research generation');
    }
  };

  // Handle progress modal close
  const handleProgressModalClose = () => {
    setShowProgressModal(false);
    setResearchParams(null);
  };

  // Handle successful research generation
  const handleResearchSuccess = (research: any) => {
    console.log('[CreateResearchModal] Research generation successful:', research);
    
    // Close both modals
    handleProgressModalClose();
    resetForm();
    onClose();
    
    toast.success('Research generated successfully! It will appear in your Generated Research section.');
    
    if (onResearchCreated) {
      onResearchCreated(research.id);
    }
  };

  return (
    <>
      <DashboardModal
        isOpen={isOpen && !showProgressModal}
        onClose={handleCancel}
        title="Create Research"
        size="md"
        className="max-w-2xl"
        accentColor="blue"
      >
      <div className="space-y-6">
        {/* Research Creation Form */}
        <div className="space-y-4">
          {/* Research Topic */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Research Topic
            </label>
            <input
              type="text"
              value={formState.researchPrompt}
              onChange={handlePromptChange}
              placeholder="e.g., How to scale your app to 1 million users"
              className="w-full px-3 py-2 text-sm rounded-lg border border-blue-200/50 dark:border-blue-800/50 bg-white/50 dark:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-colors"
              disabled={isLoading}
            />
          </div>
          
          {/* Additional Context */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Additional Context
            </label>
            <Textarea
              value={formState.description}
              onChange={handleDescriptionChange}
              placeholder="Provide any specific context or requirements for this research..."
              rows={3}
              className="resize-none border-blue-200/50 dark:border-blue-800/50 bg-white/50 dark:bg-gray-800/50 focus:ring-blue-500/50 focus:border-blue-500/50"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="flex-1"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateResearch}
            disabled={!isReady || isLoading}
            className="flex-1 group relative overflow-hidden bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {/* Button shine effect */}
            <span className="absolute inset-0 w-0 h-full bg-white opacity-20 transition-all duration-500 group-hover:w-full"></span>
            
            {/* Button content */}
            <span className="relative flex items-center justify-center gap-2">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {isLoading ? 'Generating Research...' : 'Generate Research'}
            </span>
          </Button>
        </div>
      </div>
      </DashboardModal>
      
      {/* Research Generation Modal - Beautiful Progress */}
      <ResearchGenerationModal
        isOpen={showProgressModal}
        onClose={handleProgressModalClose}
        researchParams={researchParams}
        researchMutation={generateResearchMutation}
        onSuccess={handleResearchSuccess}
      />
    </>
  );
};

export default CreateResearchModal; 